import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { AIService } from 'src/app/ai/ai.service';
import { EmployeeService } from 'src/app/employees/employees.service';
import { Employee } from 'src/app/models/Employee';
import ProjectAccount from 'src/app/models/ProjectAccount';
import { JiraLoginService } from 'src/app/shared/login-components/jira-login/jira-login.service';

import { ProjectService } from '../project.service';

@Component({
    selector: 'app-project-account-modal',
    templateUrl: './project-account-modal.component.html',
    styleUrls: ['./project-account-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ProjectAccountModalComponent implements OnInit {
    @Input() projectUuid: string;
    @Input() jiraProjectId: string | number;
    @Input() existingAccount: ProjectAccount | null = null;
    form: UntypedFormGroup;
    jiraAccounts: any[] = [];
    employees: Employee[] = [];
    loading = false;
    aiExtracting = false;

    get isEditMode(): boolean {
        return !!this.existingAccount?.uuid;
    }

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private projectService: ProjectService,
        private jiraLoginService: JiraLoginService,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private aiService: AIService,
        private employeeService: EmployeeService,
    ) {}

    ngOnInit(): void {
        this.form = this.fb.group({
            jiraAccountId: ['', [Validators.required]],
            // accountName is no longer required in the UI; prefer deriving from the selected account
            accountName: [''],
            employeeIds: [[]],
            price: [0, [Validators.required]],
            budget: [null],
            poNumber: [''],
            sesNumber: [''],
            contractAgreementNumber: [''],
            freeText: [''],
            includeProjectManagement: [false],
            effectiveFrom: [''],
            effectiveTo: [''],
            contractFile: [null],
        });

        this.employeeService.getSimpleInformationForAllEmployees().subscribe(
            (employees) => {
                this.employees = Array.isArray(employees) ? employees : [];
            },
            () => {
                this.employees = [];
            },
        );

        // Fetch tempo account links for this project (via JiraLoginService)
        const projectIdParam = this.jiraProjectId || this.projectUuid;
        this.jiraLoginService.getTempoAccountLinksForProject(projectIdParam).subscribe(
            (res) => {
                // Expect array of accounts
                this.jiraAccounts = Array.isArray(res) ? res : res?.data || [];

                // for each account fetch its full details (includes key) and build accountCode
                this.jiraAccounts.forEach((a) => {
                    const id = a.accountId || a.key || a.id;
                    if (id) {
                        this.jiraLoginService.getTempoAccountDetails(id).subscribe(
                            (det) => {
                                a.accountTitle = det?.name || det?.accountName || a.accountTitle || a.accountName || a.name || null;

                                // prefer key returned by tempo/jira
                                a.accountCode =
                                    det?.key ||
                                    det?.accountKey ||
                                    det?.code ||
                                    det?.accountName ||
                                    det?.name ||
                                    // fallback to whatever we already had
                                    a.accountName ||
                                    a.key ||
                                    a.accountKey ||
                                    a.code ||
                                    a.name ||
                                    '#' + id;
                            },
                            () => {
                                a.accountTitle = a.accountTitle || a.accountName || a.name || null;

                                // ignore, use existing values
                                a.accountCode = a.accountName || a.key || a.accountKey || a.code || a.name || '#' + id;
                            },
                        );
                    } else {
                        a.accountTitle = a.accountTitle || a.accountName || a.name || null;

                        a.accountCode = a.accountName || a.key || a.accountKey || a.code || a.name || '';
                    }
                });

                console.log('loaded jiraAccounts', this.jiraAccounts);

                if (this.existingAccount?.jiraAccountId) {
                    this.onAccountSelect(this.existingAccount.jiraAccountId);
                }
            },
            (err) => this.logger.warn('Failed to load jira accounts', err),
        );

        if (this.existingAccount) {
            this.form.patchValue({
                jiraAccountId: this.existingAccount.jiraAccountId || '',
                accountName: this.existingAccount.accountName || '',
                employeeIds: this.existingAccount.employeeIds || (this.existingAccount.employeeId ? [this.existingAccount.employeeId] : []),
                price: this.existingAccount.price,
                budget: this.existingAccount.budget,
                poNumber: this.existingAccount.poNumber || '',
                sesNumber: this.existingAccount.sesNumber || '',
                contractAgreementNumber: this.existingAccount.contractAgreementNumber || '',
                freeText: this.existingAccount.freeText || '',
                includeProjectManagement: !!this.existingAccount.includeProjectManagement,
                effectiveFrom: this.existingAccount.effectiveFrom || '',
                effectiveTo: this.existingAccount.effectiveTo || '',
                contractFile: this.existingAccount.contractUrl || null,
            });
        }
    }

    /**
     * When the dropdown value changes, look up the selected account
     * and prefill the accountName field for the user.
     */
    onAccountSelect(id: string) {
        const acc = this.jiraAccounts.find((a) => String(a.accountId || a.key || a.id) === String(id));

        if (acc) {
            const name = acc.accountName || acc.name || acc.key || acc.accountKey || acc.code || acc.accountCode;
            this.form.get('accountName')?.setValue(name || String(id));
        }
    }

    private resolveAccountName(jiraAccountId: string, providedAccountName?: string): string {
        if (providedAccountName && providedAccountName.trim()) {
            return providedAccountName.trim();
        }

        const acc = this.jiraAccounts.find((account) => String(account.accountId || account.key || account.id) === String(jiraAccountId));

        const fallback = acc?.accountCode || acc?.accountName || acc?.name || acc?.key || acc?.accountKey || acc?.code;

        return fallback && String(fallback).trim() ? String(fallback).trim() : String(jiraAccountId);
    }

    getJiraAccountOptionLabel(account: any): string {
        const code = (account?.accountCode || account?.key || account?.accountKey || account?.code || account?.accountId || account?.id || '')
            .toString()
            .trim();

        const title = (account?.accountTitle || account?.accountName || account?.name || account?.displayName || account?.description || '').toString().trim();

        if (code && title && title !== code) {
            return `${code} (${title})`;
        }

        if (code && /^\d+$/.test(code)) {
            return title ? `${title} (${code})` : `Project account (${code})`;
        }

        return code || title || 'Project account';
    }

    getEmployeeOptionLabel(employee: Employee): string {
        if (!employee) {
            return '';
        }
        return `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.uuid;
    }

    private readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const res = (reader.result as string) || '';
                const base64 = res.includes('base64,') ? res.split('base64,')[1] : res;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async extractInvoiceFieldsWithAi(): Promise<void> {
        try {
            this.aiExtracting = true;
            const contractFileValue: any = this.form.value?.contractFile || null;
            if (!contractFileValue) {
                this.toastr.info('Please upload/select a contract document first.');
                return;
            }

            let source: string;
            if (typeof contractFileValue === 'string') {
                source = contractFileValue;
            } else if (contractFileValue instanceof File) {
                const base64 = await this.readFileAsBase64(contractFileValue);
                const mime = contractFileValue.type || 'application/pdf';
                source = `data:${mime};base64,${base64}`;
            } else {
                this.toastr.error('Unsupported contract file source for AI extraction.');
                return;
            }

            const result = await this.aiService.suggestInvoiceContractFieldsFromPdfUrl(source);
            this.form.patchValue({
                price: result.pricePerHour ?? this.form.value?.price ?? 0,
                budget: result.budget ?? this.form.value?.budget ?? null,
                effectiveFrom: result.effectiveFrom || this.form.value?.effectiveFrom || '',
                effectiveTo: result.effectiveTo || this.form.value?.effectiveTo || '',
                poNumber: result.poNumber || this.form.value?.poNumber || '',
                sesNumber: result.sesNumber || this.form.value?.sesNumber || '',
                contractAgreementNumber: result.contractAgreementNumber || this.form.value?.contractAgreementNumber || '',
                freeText: result.freeText || this.form.value?.freeText || '',
            });

            if (
                !result.pricePerHour &&
                !result.budget &&
                !result.effectiveFrom &&
                !result.effectiveTo &&
                !result.poNumber &&
                !result.sesNumber &&
                !result.contractAgreementNumber &&
                !result.freeText
            ) {
                this.toastr.info('AI found no invoice reference fields in this document.');
            } else {
                this.toastr.success('AI fields extracted and prefilled.');
            }
        } catch (err: any) {
            this.logger.error('AI extraction for project account failed', err);
            this.toastr.error(err?.message || 'AI extraction failed.');
        } finally {
            this.aiExtracting = false;
        }
    }

    async submit() {
        if (!this.form.valid) return;
        this.loading = true;

        const value = this.form.value;
        const employeeIds: string[] = Array.isArray(value.employeeIds) ? value.employeeIds.filter((id: string) => !!id) : [];

        const dto: any = {
            projectId: this.projectUuid,
            jiraAccountId: value.jiraAccountId,
            accountName: this.resolveAccountName(value.jiraAccountId, value.accountName),
            price: Number(value.price),
            budget: value.budget !== null && value.budget !== '' ? Number(value.budget) : null,
            poNumber: value.poNumber || null,
            sesNumber: value.sesNumber || null,
            contractAgreementNumber: value.contractAgreementNumber || null,
            freeText: value.freeText || null,
            includeProjectManagement: !!value.includeProjectManagement,
            effectiveFrom: value.effectiveFrom || null,
            effectiveTo: value.effectiveTo || null,
            employeeId: employeeIds.length > 0 ? employeeIds[0] : null,
            employeeIds: employeeIds,
        };

        // If file-upload control contains a URL, pass it as contractUrl
        if (value.contractFile) {
            dto.contractUrl = value.contractFile;
            dto.contractFileName = this.form.get('contractFile')?.value?.split('/')[3]?.split('___')[0] || null;
        }

        try {
            if (this.isEditMode) {
                await firstValueFrom(this.projectService.updateProjectAccount(this.existingAccount.uuid, dto));
            } else {
                await firstValueFrom(this.projectService.addProjectAccount(dto));
            }

            this.loading = false;
            this.activeModal!.close('saved');
        } catch (err) {
            this.loading = false;
            this.logger!.error('Failed to save project account', err);
        }
    }
}
